"use strict";
/**
 * 对比度守卫。
 *
 * 客户在后台把主色改成浅黄，白字按钮立刻不可读——这类工单占比不低，
 * 而且客户不会认为是自己的问题。所以改色的那一刻就要算对比度并提示，
 * 而不是等上线后收到投诉。默认只警告不阻断：客户的品牌色是生意，不能因为
 * 自动检查不通过就让人没法上线。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ON_BRAND = void 0;
exports.parseHex = parseHex;
exports.relativeLuminance = relativeLuminance;
exports.contrastRatio = contrastRatio;
exports.auditContrast = auditContrast;
exports.suggestOnBrand = suggestOnBrand;
exports.toHex = toHex;
exports.mixHex = mixHex;
function parseHex(input) {
    const hex = input.trim().replace(/^#/, '');
    const full = hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    if (!/^[0-9a-fA-F]{6}$/.test(full))
        return null;
    return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
    };
}
function channel(value) {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function relativeLuminance(rgb) {
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}
/** WCAG 2.1 对比度 */
function contrastRatio(foreground, background) {
    const fg = parseHex(foreground);
    const bg = parseHex(background);
    if (!fg || !bg)
        return null;
    const l1 = relativeLuminance(fg);
    const l2 = relativeLuminance(bg);
    const [light, dark] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (light + 0.05) / (dark + 0.05);
}
const CHECKS = [
    { fg: '--color-text', bg: '--color-bg', required: 4.5, label: '正文 / 背景' },
    { fg: '--color-text-muted', bg: '--color-bg', required: 4.5, label: '次要文字 / 背景' },
    { fg: '--color-on-brand', bg: '--color-brand', required: 4.5, label: '按钮文字 / 主色' },
    { fg: '--color-text', bg: '--color-surface', required: 4.5, label: '正文 / 卡片' },
];
function auditContrast(vars) {
    const issues = [];
    for (const check of CHECKS) {
        const fg = vars[check.fg];
        const bg = vars[check.bg];
        if (!fg || !bg)
            continue;
        const ratio = contrastRatio(fg, bg);
        if (ratio === null)
            continue;
        if (ratio < check.required) {
            issues.push({
                pair: `${check.label}（${check.fg} / ${check.bg}）`,
                ratio: Math.round(ratio * 100) / 100,
                required: check.required,
                level: ratio < check.required * 0.7 ? 'error' : 'warn',
                message: `对比度 ${ratio.toFixed(2)}:1 低于 WCAG AA 要求的 ${check.required}:1，文字可能看不清`,
            });
        }
    }
    return issues;
}
/**
 * 品牌色上可用的两种字色。
 *
 * 之所以要有名字，而不是各处直接写 `'#ffffff'`：控制台需要拿"最后选了哪个"
 * 去做文案（按钮文字是白字还是深色字），而 UI 代码里出现裸色值会被
 * `no-raw-color` 拦下来 —— 那条规则的理由是"颜色必须来自令牌"，
 * 这两个值本身就是令牌的计算结果，所以在这里给它们一个名字。
 */
exports.ON_BRAND = { light: '#ffffff', dark: '#161d26' };
/** 给客户推荐一个可读的文字色：品牌色越浅越用深色字 */
function suggestOnBrand(brandColor) {
    const black = contrastRatio(exports.ON_BRAND.dark, brandColor) ?? 0;
    const white = contrastRatio(exports.ON_BRAND.light, brandColor) ?? 0;
    return black >= white ? exports.ON_BRAND.dark : exports.ON_BRAND.light;
}
const clamp255 = (v) => Math.max(0, Math.min(255, Math.round(v)));
function toHex(rgb) {
    const part = (v) => clamp255(v).toString(16).padStart(2, '0');
    return `#${part(rgb.r)}${part(rgb.g)}${part(rgb.b)}`;
}
/**
 * 按比例把 `from` 混向 `to`（`ratio` 是 `to` 的占比，0 = 原色，1 = 完全变成 `to`）。
 *
 * 用 sRGB 通道直接插值而不是先转线性光：色阶只用来看，不做物理计算，
 * 线性插值在深色上会明显偏亮，反而更不像"同一个色系"。
 */
function mixHex(from, to, ratio) {
    const a = parseHex(from);
    const b = parseHex(to);
    if (!a || !b)
        return null;
    const t = Math.max(0, Math.min(1, ratio));
    return toHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    });
}
//# sourceMappingURL=contrast.js.map